import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorScreenProps,
} from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import {
  createNativeStackNavigator,
  NativeStackNavigationProp,
} from "@react-navigation/native-stack";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import { useNavigationHeaderOptions } from "@/src/common/navigationHeaderOptions";
import { graphql, useLazyLoadQuery } from "react-relay";

import { InnerCalendarTeamEventEditScreenComponent } from "./CalendarTeamEventEdit";

import { WidgetCalendarEventEditScreenQuery } from "./__generated__/WidgetCalendarEventEditScreenQuery.graphql";

type Props = {
  navigation: CompositeNavigationProp<
    NativeStackNavigationProp<ParamList, "WidgetCalendarEventEditScreen">,
    UserLoggedInNavigatorNavigationProp<"WidgetCalendarEventEdit">
  >;
  route: RouteProp<ParamList, "WidgetCalendarEventEditScreen">;
};

type ParamList = {
  WidgetCalendarEventEditScreen: {
    widgetId: string;
    eventId: string;
    connections: string[];
  };
};

const Stack = createNativeStackNavigator<ParamList>();

function WidgetCalendarEventEditScreenComponent(
  props: Props & { fetchKey: string }
) {
  const data = useLazyLoadQuery<WidgetCalendarEventEditScreenQuery>(
    graphql`
      query WidgetCalendarEventEditScreenQuery($widgetId: ID!, $eventId: ID!) {
        widgetCalendarOverview(id: $widgetId) {
          calendars(includeExternal: true) {
            ...CalendarTeamEventEditScreenComponent_storeCalendars
          }
        }
        store {
          timezone
          calendarEvent(id: $eventId) {
            ...CalendarTeamEventEditScreenComponent_event
          }
        }
      }
    `,
    {
      widgetId: props.route.params.widgetId,
      eventId: props.route.params.eventId,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  if (!data.widgetCalendarOverview || !data.store.calendarEvent) {
    return null;
  }

  return (
    <InnerCalendarTeamEventEditScreenComponent
      onClose={() => props.navigation.goBack()}
      calendars={data.widgetCalendarOverview.calendars}
      event={data.store.calendarEvent}
      timezone={data.store.timezone}
      connections={props.route.params.connections}
    />
  );
}

function WidgetCalendarEventEditScreen(props: Props) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fk) => (
        <WidgetCalendarEventEditScreenComponent {...props} fetchKey={fk} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

export const WidgetCalendarEventEdit = withWebModal(
  (props: UserLoggedInNavigatorScreenProps<"WidgetCalendarEventEdit">) => {
    const navigationHeaderOptions = useNavigationHeaderOptions();
    return (
      <Stack.Navigator initialRouteName="WidgetCalendarEventEditScreen">
        <Stack.Screen
          name="WidgetCalendarEventEditScreen"
          component={WidgetCalendarEventEditScreen}
          initialParams={{
            widgetId: props.route.params.widgetId,
            eventId: props.route.params.eventId,
            connections: props.route.params.connections,
          }}
          options={navigationHeaderOptions({
            type: "CLOSE",
            onClose: () => props.navigation.goBack(),
            title: "",
            defaultContentStyle: true,
          })}
        />
      </Stack.Navigator>
    );
  }
);
